import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  where,
  onSnapshot,
  getDocs,
  Timestamp,
  QueryConstraint,
  writeBatch,
  doc,
  startAfter,
  DocumentSnapshot,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { db, auth } from "../utils/firebase";
import { EmailLog, EmailLogFilter } from "../types/logging";

const EMAIL_LOGS_COLLECTION = "emailLogs";

/**
 * Get the current authenticated user UID from Firebase or AWS Cognito
 */
const getCurrentUserUid = (): string | null => {
  // Check Firebase auth first
  const firebaseUser = auth.currentUser;
  if (firebaseUser?.uid) {
    return firebaseUser.uid;
  }
  
  // Check for persisted AWS Cognito auth
  try {
    const persistedUser = localStorage.getItem('aws-cognito-user');
    if (persistedUser) {
      const userData = JSON.parse(persistedUser);
      return userData.uid || null;
    }
  } catch (error) {
    // Silently handle error checking AWS Cognito auth
  }
  
  return null;
};

/**
 * Log an email send attempt to Firestore
 */
export const logEmailSend = async (emailLog: Omit<EmailLog, "id" | "timestamp">): Promise<string> => {
  try {
    const searchTokens = generateSearchTokens(emailLog);
    const docRef = await addDoc(collection(db, EMAIL_LOGS_COLLECTION), {
      ...emailLog,
      searchTokens,
      timestamp: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error("Error logging email:", error);
    throw error;
  }
};

/**
 * Create multiple email logs in a batch operation
 * @param logs Array of email logs to create
 */
export const batchCreateEmailLogs = async (logs: EmailLog[]): Promise<void> => {
  try {
    // Process in batches of 500 (Firestore batch limit)
    const batchSize = 500;
    for (let i = 0; i < logs.length; i += batchSize) {
      const batch = writeBatch(db);
      const batchLogs = logs.slice(i, i + batchSize);
      
      batchLogs.forEach((log) => {
        const docRef = doc(collection(db, EMAIL_LOGS_COLLECTION));
        batch.set(docRef, log);
      });
      
      await batch.commit();
    }
  } catch (error) {
    console.error("Error batch creating email logs:", error);
    throw error;
  }
};

/**
 * Get all existing message IDs from the database for deduplication
 * Handles authentication errors gracefully
 * @returns Set of message IDs
 */
export const getExistingMessageIds = async (): Promise<Set<string>> => {
  try {
    const q = query(
      collection(db, EMAIL_LOGS_COLLECTION),
      where("messageId", "!=", null)
    );

    const querySnapshot = await getDocs(q);
    const messageIds = new Set<string>();
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.messageId) {
        messageIds.add(data.messageId);
      }
    });
    
    return messageIds;
  } catch (error) {
    // Handle permission errors gracefully - return empty set for any auth errors
    return new Set<string>();
  }
};

/**
 * Get email logs with optional filtering and pagination
 * Handles authentication errors gracefully
 * @returns Object with logs array and lastDoc for pagination
 */
export const getEmailLogsPaginated = async (
  filters?: EmailLogFilter,
  limitCount: number = 100,
  lastDoc?: QueryDocumentSnapshot
): Promise<{ logs: EmailLog[]; lastDoc: QueryDocumentSnapshot | null; hasMore: boolean }> => {
  try {
    const constraints: QueryConstraint[] = [
      orderBy("timestamp", "desc"),
      limit(limitCount),
    ];

    // Add search constraint BEFORE other filters for optimal index usage
    if (filters?.search && filters.search.trim().length > 0) {
      const searchTerm = filters.search.trim().toLowerCase();
      constraints.unshift(where("searchTokens", "array-contains", searchTerm));
    }

    // Add other filters
    if (filters?.startDate) {
      constraints.push(where("timestamp", ">=", Timestamp.fromDate(filters.startDate)));
    }
    if (filters?.endDate) {
      constraints.push(where("timestamp", "<=", Timestamp.fromDate(filters.endDate)));
    }
    if (filters?.emailType && filters.emailType !== "all") {
      constraints.push(where("emailType", "==", filters.emailType));
    }
    if (filters?.status && filters.status !== "all") {
      constraints.push(where("status", "==", filters.status));
    }

    // Add pagination cursor if provided
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    const q = query(collection(db, EMAIL_LOGS_COLLECTION), ...constraints);
    const querySnapshot = await getDocs(q);

    const logs: EmailLog[] = [];
    querySnapshot.forEach((doc) => {
      logs.push({
        id: doc.id,
        ...doc.data(),
      } as EmailLog);
    });

    // Get the last document for next page
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    const hasMore = querySnapshot.docs.length === limitCount;

    return { logs, lastDoc: lastVisible, hasMore };
  } catch (error) {
    // Handle permission errors gracefully - return empty logs for any auth errors
    return { logs: [], lastDoc: null, hasMore: false };
  }
};

/**
 * Get email logs with optional filtering (legacy - no pagination)
 * Handles authentication errors gracefully
 */
export const getEmailLogs = async (
  filters?: EmailLogFilter,
  limitCount: number = 100
): Promise<EmailLog[]> => {
  try {
    const constraints: QueryConstraint[] = [
      orderBy("timestamp", "desc"),
      limit(limitCount),
    ];

    // Add search constraint BEFORE other filters for optimal index usage
    if (filters?.search && filters.search.trim().length > 0) {
      const searchTerm = filters.search.trim().toLowerCase();
      constraints.unshift(where("searchTokens", "array-contains", searchTerm));
    }

    // Add other filters
    if (filters?.startDate) {
      constraints.push(where("timestamp", ">=", Timestamp.fromDate(filters.startDate)));
    }
    if (filters?.endDate) {
      constraints.push(where("timestamp", "<=", Timestamp.fromDate(filters.endDate)));
    }
    if (filters?.emailType && filters.emailType !== "all") {
      constraints.push(where("emailType", "==", filters.emailType));
    }
    if (filters?.status && filters.status !== "all") {
      constraints.push(where("status", "==", filters.status));
    }

    const q = query(collection(db, EMAIL_LOGS_COLLECTION), ...constraints);
    const querySnapshot = await getDocs(q);

    const logs: EmailLog[] = [];
    querySnapshot.forEach((doc) => {
      logs.push({
        id: doc.id,
        ...doc.data(),
      } as EmailLog);
    });

    return logs;
  } catch (error) {
    // Handle permission errors gracefully - return empty logs for any auth errors
    return [];
  }
};

/**
 * Subscribe to email logs with real-time updates
 * Handles authentication errors gracefully
 */
export const subscribeToEmailLogs = (
  callback: (logs: EmailLog[]) => void,
  filters?: EmailLogFilter,
  limitCount: number = 100
): (() => void) => {
  try {
    const constraints: QueryConstraint[] = [
      orderBy("timestamp", "desc"),
      limit(limitCount),
    ];

    // Add search constraint BEFORE other filters for optimal index usage
    if (filters?.search && filters.search.trim().length > 0) {
      const searchTerm = filters.search.trim().toLowerCase();
      constraints.unshift(where("searchTokens", "array-contains", searchTerm));
    }

    // Add other filters
    if (filters?.startDate) {
      constraints.push(where("timestamp", ">=", Timestamp.fromDate(filters.startDate)));
    }
    if (filters?.endDate) {
      constraints.push(where("timestamp", "<=", Timestamp.fromDate(filters.endDate)));
    }
    if (filters?.emailType && filters.emailType !== "all") {
      constraints.push(where("emailType", "==", filters.emailType));
    }
    if (filters?.status && filters.status !== "all") {
      constraints.push(where("status", "==", filters.status));
    }

    const q = query(collection(db, EMAIL_LOGS_COLLECTION), ...constraints);

    return onSnapshot(q, 
      (querySnapshot) => {
        const logs: EmailLog[] = [];
        querySnapshot.forEach((doc) => {
          logs.push({
            id: doc.id,
            ...doc.data(),
          } as EmailLog);
        });

        callback(logs);
      },
      (error) => {
        // Handle permission errors gracefully - return empty logs for any auth errors
        callback([]);
      }
    );
  } catch (error) {
    callback([]); // Return empty logs on setup error
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Generate search tokens from email log fields
 * 
 * Extracts and normalizes searchable tokens from various fields of an EmailLog object.
 * This enables efficient client-side searching without requiring full-text search indexes.
 * 
 * Tokenization rules:
 * - Email addresses are split by '@' and '.' to create multiple searchable parts
 * - Names are split by whitespace, each word becomes a token
 * - Subject text is split by whitespace, words shorter than 3 chars are skipped
 * - Email type and status are lowercased and added as tokens
 * - Month and year are added directly for date-based searching
 * - All tokens are lowercased for case-insensitive searching
 * - Duplicate tokens are automatically removed (via Set)
 * 
 * Performance Characteristics:
 * - O(n) time complexity where n is the total length of all input strings
 * - Uses Set for O(1) duplicate prevention
 * - No mutations of input object (pure function)
 * 
 * @param log Partial EmailLog object with fields to tokenize
 * @returns Array of unique lowercase search tokens
 * 
 * @example
 * const tokens = generateSearchTokens({
 *   recipientEmail: 'john.doe@example.com',
 *   recipientName: 'John Doe',
 *   subject: 'Monthly Financial Report',
 *   emailType: 'financial',
 *   status: 'sent'
 * });
 * // Returns: ['john.doe@example.com', 'john.doe', 'john', 'doe', 'example.com', 'example', 'com', 
 * //            'monthly', 'financial', 'report', 'sent']
 */

/**
 * Helper function to tokenize email addresses
 * Splits email by @ and . to extract both local and domain parts
 * @param email Email address to tokenize
 * @param tokens Set to add tokens to
 */
const tokenizeEmail = (email: string, tokens: Set<string>): void => {
  const lowerEmail = email.toLowerCase();
  tokens.add(lowerEmail);
  
  // Split by @ to get local and domain parts
  const emailParts = lowerEmail.split('@');
  for (let i = 0; i < emailParts.length; i++) {
    const part = emailParts[i];
    tokens.add(part);
    
    // Split domain by dots for subdomain searching
    const subparts = part.split('.');
    for (let j = 0; j < subparts.length; j++) {
      const subpart = subparts[j];
      if (subpart.length > 0) {
        tokens.add(subpart);
      }
    }
  }
};

/**
 * Helper function to tokenize recipient names
 * Splits names by whitespace, includes all words regardless of length
 * @param name Recipient name to tokenize
 * @param tokens Set to add tokens to
 */
const tokenizeName = (name: string, tokens: Set<string>): void => {
  const lowerName = name.toLowerCase();
  // Split by unicode-aware whitespace pattern
  const words = lowerName.split(/\s+/);
  
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (word.length > 0) {
      tokens.add(word);
    }
  }
};

/**
 * Helper function to tokenize subject line
 * Splits subject by whitespace, skips words with 2 or fewer characters
 * @param subject Email subject to tokenize
 * @param tokens Set to add tokens to
 */
const tokenizeSubject = (subject: string, tokens: Set<string>): void => {
  const lowerSubject = subject.toLowerCase();
  // Split by unicode-aware whitespace pattern
  const words = lowerSubject.split(/\s+/);
  
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    if (word.length > 2) {
      tokens.add(word);
    }
  }
};

/**
 * Helper function to add string tokens
 * Lowercases the value and adds to token set
 * @param value Value to add as token
 * @param tokens Set to add token to
 */
const addStringToken = (value: string | undefined, tokens: Set<string>): void => {
  if (value && value.length > 0) {
    tokens.add(value.toLowerCase());
  }
};

export const generateSearchTokens = (log: Partial<EmailLog>): string[] => {
  // Input validation - return empty array for invalid input
  if (!log || typeof log !== 'object') {
    return [];
  }
  
  const tokens = new Set<string>();
  
  // Email tokenization (handles complex domains and subdomains)
  if (log.recipientEmail && typeof log.recipientEmail === 'string') {
    tokenizeEmail(log.recipientEmail, tokens);
  }
  
  // Name tokenization (all words included)
  if (log.recipientName && typeof log.recipientName === 'string') {
    tokenizeName(log.recipientName, tokens);
  }
  
  // Subject tokenization (words must be 3+ characters)
  if (log.subject && typeof log.subject === 'string') {
    tokenizeSubject(log.subject, tokens);
  }
  
  // Type and status tokens
  if (log.emailType && typeof log.emailType === 'string') {
    addStringToken(log.emailType, tokens);
  }
  if (log.status && typeof log.status === 'string') {
    addStringToken(log.status, tokens);
  }
  
  // Date tokens
  if (log.month && typeof log.month === 'string') {
    addStringToken(log.month, tokens);
  }
  if (log.year && typeof log.year === 'string') {
    addStringToken(log.year, tokens);
  }
  
  // Convert Set to sorted array for consistent results (helps with testing and caching)
  return Array.from(tokens);
};

/**
 * Get all email logs for a specific batch
 * Used to display detailed results for a batch send operation
 * @param batchId The batch ID to query
 * @returns Array of email logs for that batch
 */
export const getEmailLogsByBatchId = async (batchId: string): Promise<EmailLog[]> => {
  try {
    const q = query(
      collection(db, EMAIL_LOGS_COLLECTION),
      where("batchId", "==", batchId),
      orderBy("timestamp", "desc")
    );

    const querySnapshot = await getDocs(q);
    const logs: EmailLog[] = [];

    querySnapshot.forEach((doc) => {
      logs.push({
        id: doc.id,
        ...doc.data(),
      } as EmailLog);
    });

    return logs;
  } catch (error) {
    console.error("Error getting logs by batch ID:", error);
    return [];
  }
};

/**
 * Subscribe to real-time email logs for a specific batch
 * Used by progress modal to display live results
 * @param batchId The batch ID to subscribe to
 * @param callback Function called with updated logs
 * @returns Unsubscribe function
 */
export const subscribeToEmailLogsByBatchId = (
  batchId: string,
  callback: (logs: EmailLog[]) => void
): (() => void) => {
  try {
    const q = query(
      collection(db, EMAIL_LOGS_COLLECTION),
      where("batchId", "==", batchId),
      orderBy("timestamp", "desc")
    );

    return onSnapshot(
      q,
      (querySnapshot) => {
        const logs: EmailLog[] = [];
        querySnapshot.forEach((doc) => {
          logs.push({
            id: doc.id,
            ...doc.data(),
          } as EmailLog);
        });
        callback(logs);
      },
      (error) => {
        console.error("Error subscribing to batch logs:", error);
        callback([]);
      }
    );
  } catch (error) {
    console.error("Error setting up batch logs subscription:", error);
    callback([]);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * Get summary statistics for a batch from email logs
 * Counts sent/failed/total emails for a batch
 * @param batchId The batch ID to analyze
 * @returns Object with sent, failed, and total counts
 */
export const getBatchLogsSummary = async (
  batchId: string
): Promise<{ sent: number; failed: number; total: number }> => {
  try {
    const q = query(
      collection(db, EMAIL_LOGS_COLLECTION),
      where("batchId", "==", batchId)
    );

    const querySnapshot = await getDocs(q);
    let sent = 0;
    let failed = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data() as EmailLog;
      if (data.status === "sent") {
        sent++;
      } else if (data.status === "failed") {
        failed++;
      }
    });

    const total = querySnapshot.size;

    return { sent, failed, total };
  } catch (error) {
    console.error("Error getting batch logs summary:", error);
    return { sent: 0, failed: 0, total: 0 };
  }
};